import moment from 'moment-timezone';

/**
 * IANA zone used for preset slots (today 6pm / tomorrow …) and for timing-option labels.
 * Candidate zone when valid; otherwise the recruiter's zone.
 */
export function getPresetScheduleAnchorTimezoneKey(
  hasCandidateTimezone,
  candidateTimezoneKey,
  userTimezoneKey
) {
  return hasCandidateTimezone ? candidateTimezoneKey : userTimezoneKey;
}

/**
 * KSelect options: wall times are interpreted in `displayTimezoneKey` (candidate or recruiter fallback).
 * Omits "Today at 6:00 PM" when 6:00 PM today in that zone is not strictly in the future.
 * Abbreviation comes from the wall-clock instant (e.g. HDT vs HST for America/Adak), not static tables.
 */
export function getScheduleTimingSelectOptions({
  hasCandidateTimezone,
  displayTimezoneKey,
}) {
  const todaySixPmInZone = moment()
    .tz(displayTimezoneKey)
    .startOf('day')
    .add(18, 'hours');
  const showTodayEvening = todaySixPmInZone.isAfter(moment());

  const anchor = moment().tz(displayTimezoneKey);
  const tomorrowMorning = anchor
    .clone()
    .add(1, 'day')
    .startOf('day')
    .add(9, 'hours');
  const tomorrowAfternoon = anchor
    .clone()
    .add(1, 'day')
    .startOf('day')
    .add(14, 'hours');
  const tomorrowEvening = anchor
    .clone()
    .add(1, 'day')
    .startOf('day')
    .add(18, 'hours');

  const options = [{ value: 'immediately', label: 'Now (Immediately)' }];
  if (showTodayEvening) {
    options.push({
      value: 'today_evening',
      label: `Today at 6:00 PM ${todaySixPmInZone.format('z')}`,
    });
  }
  options.push(
    {
      value: 'tomorrow_morning',
      label: `Tomorrow at 9:00 AM ${tomorrowMorning.format('z')}`,
    },
    {
      value: 'tomorrow_afternoon',
      label: `Tomorrow at 2:00 PM ${tomorrowAfternoon.format('z')}`,
    },
    {
      value: 'tomorrow_evening',
      label: `Tomorrow at 6:00 PM ${tomorrowEvening.format('z')}`,
    }
  );
  if (hasCandidateTimezone) {
    options.push({ value: 'custom', label: 'Custom date' });
  }
  return options;
}

/** True when contact has a non-empty IANA timezone known to moment-timezone. */
export function hasValidCandidateTimezone(contact) {
  const tz = contact?.time_zone;
  return Boolean(tz && typeof tz === 'string' && moment.tz.zone(tz));
}

/** Parses { date, time, timezoneKey } into a moment in that zone, or null if incomplete/invalid. */
export function parseCustomScheduleInstant(customSchedule) {
  if (
    !customSchedule?.date ||
    !customSchedule?.time ||
    !customSchedule?.timezoneKey
  ) {
    return null;
  }
  const m = moment.tz(
    `${moment(customSchedule.date).format('YYYY-MM-DD')} ${customSchedule.time}`,
    customSchedule.timezoneKey
  );
  return m.isValid() ? m : null;
}

/** Whether the chosen local wall time is strictly after now (UTC), with a small buffer. */
export function isCustomScheduleInFuture(customSchedule, bufferMinutes = 1) {
  const m = parseCustomScheduleInstant(customSchedule);
  if (!m) return false;
  return m.isAfter(moment().subtract(bufferMinutes, 'minutes'));
}
